/**
 * Admin Collections Page
 *
 * Displays list of all collections with search and pagination.
 * Allows sorting and navigation to collection detail pages.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getCollections } from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { CollectionsTable } from '../components/admin/CollectionsTable.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import type { CollectionsListResponse } from '../types/admin.js';

const PAGE_SIZE = 50;

export function AdminCollectionsPage() {
  const [data, setData] = useState<CollectionsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'name' | 'creator_address' | 'created_at' | 'total_supply' | 'floor_price'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const loadCollections = useCallback(async (params?: {
    sort?: typeof sortColumn;
    order?: typeof sortOrder;
    searchOverride?: string;
    offsetOverride?: number;
  }) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCollections({
        sort: params?.sort ?? sortColumn,
        order: params?.order ?? sortOrder,
        limit: PAGE_SIZE,
        offset: params?.offsetOverride ?? offset,
        search: (params?.searchOverride ?? search) || undefined,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [sortColumn, sortOrder, search, offset]);

  useEffect(() => {
    loadCollections();
  }, [offset]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setOffset(0);
      loadCollections({ searchOverride: value, offsetOverride: 0 });
    }, 300);
  };

  const handleSort = (sort: typeof sortColumn, order: 'asc' | 'desc') => {
    setSortColumn(sort);
    setSortOrder(order);
    setOffset(0);
    loadCollections({ sort, order, offsetOverride: 0 });
  };

  const totalCount = data?.count ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Calculate stats from current page (these are approximate when paginated)
  const verifiedCount = data?.collections.filter(c => c.isVerified).length || 0;
  const topCommunityCount = data?.collections.filter(c => c.isTopCommunity).length || 0;
  const totalSupply = data?.collections.reduce((sum, c) => sum + Number(c.totalSupply || 0), 0) || 0;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Collections</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage and monitor NFT collections
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/admin/collections/add"
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              + Add Collection
            </a>
            <button
              onClick={() => loadCollections()}
              disabled={loading}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {data && (
          <div className="mb-6 grid gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">{search ? 'Matching' : 'Total'} Collections</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{totalCount}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Verified</div>
              <div className="mt-1 text-2xl font-bold text-blue-600">{verifiedCount}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Top Community</div>
              <div className="mt-1 text-2xl font-bold text-purple-600">{topCommunityCount}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Total NFTs</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{totalSupply.toLocaleString()}</div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name, slug, issuer address, or ID..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={() => loadCollections()} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading collections...</p>
            </div>
          </div>
        )}

        {/* Collections Table */}
        {data && (
          <div className={loading ? 'pointer-events-none opacity-60' : ''}>
            <CollectionsTable collections={data.collections} onSort={handleSort} />
          </div>
        )}

        {/* Pagination */}
        {data && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-6 py-3 shadow-sm">
            <div className="text-sm text-gray-600">
              Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="px-2 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= totalCount}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
